import React, { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import venueLocationService from '../../services/venueLocationService';
import inventoryImportService, {
  downloadExistingItemsAsImport,
  extractInventoryImportError,
} from '../../services/inventoryImportService';
import type { VenueLocation } from '../../types/venue';

interface Props {
  venueId: string;
  /** Export what the list shows now, filters and all (the list owns this). */
  onExportView: () => void;
  exportingView: boolean;
  disabled?: boolean;
}

/**
 * IMPORT-ROUNDTRIP.1 — one Export, two jobs.
 *
 * "This view" is a spreadsheet of what is on screen, for reading and sharing.
 * "Bulk-edit file" is the import format for one location, to edit and upload
 * back through Import. The second used to live only inside the Import dialog,
 * where nobody looking to export would think to find it.
 */
const InventoryExportMenu: React.FC<Props> = ({ venueId, onExportView, exportingView, disabled }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openBulk = () => {
    setAnchor(null);
    setBulkOpen(true);
    setError(null);
    setLocationsLoading(true);
    // RECEIVE — the locations Import may write to, so the file can go back in.
    venueLocationService.getLocations(venueId, 'RECEIVE')
      .then(locs => {
        setLocations(locs);
        setLocationId(prev => prev || (locs.find(l => l.isDefault)?._id ?? locs[0]?._id ?? ''));
      })
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  };

  const downloadBulk = async () => {
    if (!locationId) return;
    setDownloading(true);
    setError(null);
    try {
      const loc = locations.find(l => l._id === locationId);
      const rows = await inventoryImportService.exportItems(venueId, locationId);
      downloadExistingItemsAsImport(rows, loc ? (loc.displayName ?? loc.name) : null);
      setBulkOpen(false);
    } catch (err) {
      setError(extractInventoryImportError(err));
    } finally {
      setDownloading(false);
    }
  };

  const busy = exportingView || downloading;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={busy ? <CircularProgress size={14} /> : <DownloadIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={e => setAnchor(e.currentTarget)}
        disabled={disabled || busy}
        aria-haspopup="menu"
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
      >
        {busy ? 'Exporting…' : 'Export'}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); onExportView(); }}>
          <ListItemText
            primary="This view (CSV)"
            secondary="What's on screen now, with your filters"
          />
        </MenuItem>
        <MenuItem onClick={openBulk}>
          <ListItemText
            primary="Bulk-edit file (.xlsx)…"
            secondary="Every item for one location, to edit and upload with Import"
          />
        </MenuItem>
      </Menu>

      <Dialog open={bulkOpen} onClose={downloading ? undefined : () => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Download a bulk-edit file</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth size="small" disabled={locationsLoading}>
            <InputLabel>Location</InputLabel>
            <Select value={locationId} label="Location" onChange={e => setLocationId(e.target.value)}>
              {locations.map(l => (
                <MenuItem key={l._id} value={l._id}>{l.displayName ?? l.name}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Par Level, Reorder Point and Bin Location are per location, so the file carries this
              one&apos;s, plus what&apos;s on hand there.
            </FormHelperText>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Edit it and upload it with <strong>Import</strong>. A blank cell keeps the current value;
            type <strong>-</strong> to clear one. An import takes up to 500 rows at a time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkOpen(false)} disabled={downloading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={downloadBulk}
            disabled={!locationId || downloading}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {downloading ? 'Downloading…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InventoryExportMenu;
